import { Component } from 'react';
import axios from 'axios';
import { Box, Button, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
// import UserMenu from './UserMenu';

import NZ2026Home from './NZ2026Home';
import NZ2026SeatComparison from './NZ2026SeatComparison';
import VotersDemographics from './VotersDemographics';
import VotersSeats from './VotersSeats';
import PolicyView from './PolicyView';
import NZ2026Summary from './NZ2026Summary';
import PolicySummary from './PolicySummary';
import SegmentPage from "./SegmentPage"


const NAV_ITEMS = [
  {
    key: 'NZ2026',
    label: 'NZ 2026',
    children: [
      { value: 'NZ2026/home', label: 'Home' },
      { value: 'NZ2026/seatComparison', label: 'Vote Change' },
      { value: 'NZ2026/summary', label: 'Summary' },
    ],
  },
  {
    key: 'voters',
    label: 'Voter Breakdown',
    children: [
      { value: 'voters/demographics', label: 'Demographics' },
      { value: 'voters/seats', label: 'Profiles' },
    ],
  },
  {
    key: 'policy',
    label: 'Policy',
    children: [
      { value: 'policy/scorecard', label: 'Scorecard' },
      { value: 'policy/summary', label: 'Summary' },
    ],
  },
  {
    key: 'segments',
    label: 'Segments',
    children: [{ value: 'segments/Segments', label: 'Segments' }],
  },
];

const ALL_ALLOWED = new Set(
  NAV_ITEMS.flatMap((item) => item.children.map((child) => child.value))
);

const getParentKey = (selectedSection) => {
  const match = NAV_ITEMS.find((item) =>
    item.children.some((child) => child.value === selectedSection)
  );
  return match ? match.key : NAV_ITEMS[0].key;
};

const getParentItem = (selectedSection) => {
  const parentKey = getParentKey(selectedSection);
  return NAV_ITEMS.find((item) => item.key === parentKey) || NAV_ITEMS[0];
};

const getSectionTitle = (selectedSection) => {
  for (const item of NAV_ITEMS) {
    const child = item.children.find((c) => c.value === selectedSection);
    if (child) return child.label;
  }
  return '';
};

export default class Home extends Component {
  constructor(props) {
    super(props);

    const initialTheme = 'NZ2026/home';

    this.state = {
      selectedSection: initialTheme,
      sectionTitle: getSectionTitle(initialTheme),
      user: { email: '', name: '', company: '' },
      constituencyMap: null,
      SA3Map: null,
      SA2Map: null,
      loading: true,
    };
  }

  componentDidMount() {
    this._onPopState = () => {
      const selectedSection = this.state.selectedSection;
      this.applyTheme(selectedSection);
    };

    window.addEventListener('popstate', this._onPopState);

    axios
      .get('/api/user-info/')
      .then((response) => {
        this.setState({ user: response.data });
      })
      .catch((error) => {
        console.error('Error fetching user info:', error);
      });

    const loadFGB = async (url) => {
      const { geojson } = await import('flatgeobuf');
      const resp = await fetch(url);
      const features = [];
      for await (const feature of geojson.deserialize(resp.body)) {
        features.push(feature);
      }
      return { type: 'FeatureCollection', features };
    };

    Promise.all([
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandConstituency.fgb'),
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandSA3.fgb'),
      loadFGB(process.env.PUBLIC_URL + '/NZ_maps/newZealandSA2.fgb'),
    ])
      .then(([constituencyData, sa3Data, sa2Data]) => {
        this.setState({
          constituencyMap: constituencyData,
          SA3Map: sa3Data,
          SA2Map: sa2Data,
          loading: false,
        });
      })
      .catch((error) => {
        console.error('Error loading map data:', error);
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this._onPopState);
  }

  applyTheme = (theme) => {
    if (!ALL_ALLOWED.has(theme)) return;

    this.setState({
      selectedSection: theme,
      sectionTitle: getSectionTitle(theme),
    });
  };

  handleParentChange = (parentKey) => {
    const parent = NAV_ITEMS.find((item) => item.key === parentKey);
    if (!parent || !parent.children.length) return;

    this.applyTheme(parent.children[0].value);
  };

  renderContent = () => {
    const { selectedSection, SA2Map, SA3Map, constituencyMap } = this.state;

    const sharedProps = {
      SA2Map,
      SA3Map,
      constituencyMap,
    };

    switch (selectedSection) {
      case 'NZ2026/home':
        return <NZ2026Home {...sharedProps} />;
      case 'NZ2026/seatComparison':
        return <NZ2026SeatComparison {...sharedProps} />;
      case 'NZ2026/summary':
        return <NZ2026Summary {...sharedProps} />;
      case 'voters/demographics':
        return <VotersDemographics {...sharedProps} />;
      case 'voters/seats':
        return <VotersSeats {...sharedProps} />;
      case 'policy/scorecard':
        return <PolicyView {...sharedProps} />;
      case 'policy/summary':
        return <PolicySummary {...sharedProps} />;
      case 'segments/Segments':
        return <SegmentPage {...sharedProps} />;
      default:
        return null;
    }
  };

  renderNav = () => {
    const { selectedSection } = this.state;
    const activeParent = getParentItem(selectedSection);

    return (
      <Stack
        spacing={0.25}
        sx={{
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          py: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            rowGap: 0.15,
            lineHeight: 1,
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = item.key === activeParent.key;
            const isDisabled = !item.children.length;

            return (
              <Button
                key={item.key}
                disableElevation
                disableRipple
                disabled={isDisabled}
                onClick={() => this.handleParentChange(item.key)}
                sx={{
                  minWidth: 'auto',
                  minHeight: 22,
                  height: 22,
                  px: 0.8,
                  py: 0,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: isActive ? 800 : 700,
                  fontSize: '0.95rem',
                  lineHeight: 1,
                  color: isActive ? '#111827' : '#6B7280',
                  backgroundColor: 'transparent',
                  borderBottom: isActive ? '2px solid #111827' : '2px solid transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#111827',
                    borderBottom: '2px solid #D1D5DB',
                  },
                  '&.Mui-disabled': {
                    color: '#9CA3AF',
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={0.4}
          sx={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            rowGap: 0.35,
            lineHeight: 1,
          }}
        >
          {activeParent.children.map((child) => {
            const isActive = selectedSection === child.value;

            return (
              <Button
                key={child.value}
                variant={isActive ? 'contained' : 'text'}
                disableElevation
                onClick={() => this.applyTheme(child.value)}
                sx={{
                  minWidth: 'auto',
                  minHeight: 18,
                  height: 18,
                  padding: '0 8px',
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  ...(isActive
                    ? {
                        backgroundColor: '#111827',
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#1F2937',
                        },
                      }
                    : {
                        color: '#4B5563',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                          borderColor: '#D1D5DB',
                        },
                      }),
                }}
              >
                {child.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>
    );
  };

  render() {
  const { loading } = this.state;
  const HEADER_HEIGHT = 56;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={(theme) => ({
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 20,
          backgroundColor: theme.palette.background.paper,
          borderBottom: '1px solid #E5E7EB',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            height: HEADER_HEIGHT,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            px: 2,
            py: 0.25,
            gap: 1.5,
            boxSizing: 'border-box',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src={process.env.PUBLIC_URL + '/static/images/stack_logo.png'}
              alt="District Profiles"
              style={{ height: 34, objectFit: 'contain' }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {this.renderNav()}
          </Box>

          {/*
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <UserMenu user={this.state.user} onLogout={this.handleLogout} />
          </Box>
          */}
        </Box>
      </Box>

      <Box sx={{ height: `calc(100dvh - ${HEADER_HEIGHT + 1}px)`, overflow: 'hidden' }}>
        {this.renderContent()}
      </Box>
    </Box>
  );
}
}